// ============================================================
// Chess Arena — Ability Manager
// ============================================================
// Manages in-match Ability state (Bomb, Freeze, Shield, Lightning).
// Handles activation, targeting mode, charge consumption,
// and timed effect tracking.
// ============================================================

import { AbilityConfigEntry, AbilityTargeting, GridPos } from '../core/types';
import eventBus from '../core/eventBus';
import { playerState } from '../core/playerState';
import gameConfig from '../core/gameConfig';

interface ActiveEffect {
  abilityId: string;
  expiresAt: number; // elapsed game time in seconds
}

interface TargetingState {
  abilityId: string;
  targetingType: AbilityTargeting;
}

export class AbilityManager {
  private abilityConfigs: Record<string, AbilityConfigEntry> = {};
  private equippedAbilities: string[] = [];
  private activeEffects: ActiveEffect[] = [];
  private targeting: TargetingState | null = null;
  private cooldownUntil: Record<string, number> = {};
  private currentTimeSec = 0;

  init(): void {
    const cfg = gameConfig.getAbilityConfig();
    if (!cfg) return;

    this.abilityConfigs = cfg.abilities;
    this.equippedAbilities = [...playerState.equippedAbilities];
    this.activeEffects = [];
    this.targeting = null;
    this.cooldownUntil = {};
    this.currentTimeSec = 0;
  }

  /**
   * Attempt to activate an ability. Returns true if activation started.
   * For global/auto: executes immediately.
   * For cell/enemy_piece: enters targeting mode.
   */
  activateAbility(abilityId: string): boolean {
    const config = this.abilityConfigs[abilityId];
    if (!config) return false;

    // Check equipped
    if (!this.equippedAbilities.includes(abilityId)) return false;

    // Check charges
    if (playerState.getCharges(abilityId) <= 0) return false;

    // Check cooldown
    if (this.isOnCooldown(abilityId)) return false;

    // Already in targeting mode?
    if (this.targeting) return false;

    const targetingType = config.targeting;

    if (targetingType === 'global' || targetingType === 'auto') {
      // Execute immediately
      this.executeAbility(abilityId);
      return true;
    }

    // Enter targeting mode
    this.targeting = { abilityId, targetingType };
    console.log('[AbilityManager] Entering targeting mode for', abilityId, 'type:', targetingType);
    eventBus.emit('targeting_mode_start', {
      abilityId,
      targetingType,
    });
    console.log('[AbilityManager] After emit, targeting is:', this.targeting);
    return true;
  }

  /**
   * Confirm a target during targeting mode.
   * Called by BattleScene when the player taps a valid target.
   */
  confirmTarget(pos: GridPos, pieceId?: string): boolean {
    if (!this.targeting) return false;

    const { abilityId } = this.targeting;
    this.targeting = null;

    eventBus.emit('targeting_mode_confirm', { abilityId });
    this.executeAbility(abilityId, pos, pieceId);
    return true;
  }

  /**
   * Cancel targeting mode without consuming a charge.
   */
  cancelTargeting(): void {
    if (!this.targeting) return;
    console.log('[AbilityManager] cancelTargeting called', new Error().stack?.split('\n').slice(1, 4).join(' <- '));
    const { abilityId } = this.targeting;
    this.targeting = null;
    eventBus.emit('targeting_mode_cancel', { abilityId });
  }

  isInTargetingMode(): boolean {
    return this.targeting !== null;
  }

  getTargetingState(): TargetingState | null {
    return this.targeting;
  }

  /**
   * Execute an ability effect. Consumes a charge and emits the effect event.
   */
  private executeAbility(abilityId: string, pos?: GridPos, pieceId?: string): void {
    const config = this.abilityConfigs[abilityId];
    if (!config) return;

    // Consume charge
    if (!playerState.useCharge(abilityId)) return;

    // Start cooldown
    const cdSec = config.cooldownSec ?? 0;
    if (cdSec > 0) {
      this.cooldownUntil[abilityId] = this.currentTimeSec + cdSec;
    }

    // Emit analytics event
    eventBus.emit('ability_used', { ability: abilityId });

    // Execute effect based on ability type
    switch (abilityId) {
      case 'bomb':
        // Bomb auto-targets a random enemy piece — BattleScene picks the target
        eventBus.emit('ability_bomb', {});
        break;

      case 'freeze':
        eventBus.emit('ability_freeze', {
          durationSec: config.effectDurationSec ?? 4,
        });
        break;

      case 'shield':
        eventBus.emit('ability_shield', {
          durationSec: config.effectDurationSec ?? 5,
        });
        break;

      case 'lightning':
        eventBus.emit('ability_lightning', {
          pieceId,
          pos,
        });
        break;
    }

    eventBus.emit('ability_activated', { abilityId });
  }

  /**
   * Update timed effects (freeze/shield expiry).
   * Called each frame with elapsed game time.
   */
  update(elapsedTimeSec: number): void {
    this.currentTimeSec = elapsedTimeSec;
    this.activeEffects = this.activeEffects.filter(e => e.expiresAt > elapsedTimeSec);
  }

  /**
   * Register a timed effect (called by BattleScene when freeze/shield starts).
   */
  addActiveEffect(abilityId: string, durationSec: number, currentTimeSec: number): void {
    this.activeEffects.push({
      abilityId,
      expiresAt: currentTimeSec + durationSec,
    });
  }

  /**
   * Re-read equipped abilities from playerState without resetting
   * targeting or match state. Used when abilities are granted mid-match.
   */
  refreshEquippedAbilities(): void {
    this.equippedAbilities = [...playerState.equippedAbilities];
    // Also reload ability configs in case they weren't loaded initially
    const cfg = gameConfig.getAbilityConfig();
    if (cfg) {
      this.abilityConfigs = cfg.abilities;
    }
  }

  getEquippedAbilities(): string[] {
    return this.equippedAbilities;
  }

  getAbilityConfig(abilityId: string): AbilityConfigEntry | undefined {
    return this.abilityConfigs[abilityId];
  }

  getAllAbilityConfigs(): Record<string, AbilityConfigEntry> {
    return this.abilityConfigs;
  }

  isAbilityAvailable(abilityId: string): boolean {
    const config = this.abilityConfigs[abilityId];
    if (!config) return false;
    return playerState.getCharges(abilityId) > 0
      && !this.isOnCooldown(abilityId)
      && !this.targeting;
  }

  isOnCooldown(abilityId: string): boolean {
    const until = this.cooldownUntil[abilityId];
    return until !== undefined && this.currentTimeSec < until;
  }

  /** Returns 0..1 cooldown progress (1 = just started, 0 = ready). */
  getCooldownFraction(abilityId: string): number {
    const until = this.cooldownUntil[abilityId];
    if (until === undefined || this.currentTimeSec >= until) return 0;
    const config = this.abilityConfigs[abilityId];
    const total = config?.cooldownSec ?? 0;
    if (total <= 0) return 0;
    return Math.min(1, (until - this.currentTimeSec) / total);
  }

  destroy(): void {
    this.abilityConfigs = {};
    this.equippedAbilities = [];
    this.activeEffects = [];
    this.targeting = null;
    this.cooldownUntil = {};
  }
}
